import { useCallback, useMemo, useRef, useState } from "react"
import { useMap, useMountedState, useUnmount } from "react-use"
import * as VW from "voximplant-websdk"

import { useDelayedConnectionAnalyzer } from "./useDelayedConnectionAnalyzer"

import { useInputDevicesContext } from "modules/Call/InputDevices"
import { Call, Endpoints, EventHandlers, VoximplantSdk } from "modules/Call/sdk"
import { PageType } from "modules/Call/common/constants/pageType"
import { CallFailedError, speakerIndicator } from "modules/Call/common/utils"

export interface CallsValue {
    endpoints: Endpoints
    endpointIds: string[]
    endpointsCount: number
    call: Call | undefined
    error: Error | undefined
    isMyConnectionBad: boolean
    isPeerConnectionBad: boolean
    navigateTo: PageType | undefined
}

export interface CallHandlers {
    join(): void
    leave(): Promise<void>
}

interface Parameters {
    bookingHash: string
    remaining?: number
    voximplantUserName: string
}

//useVideoCall – единый хук, который:
// 1) Инициализирует SDK (voximplant-websdk) и держит ссылку на текущий звонок
// 2) Подключается к конференции (join)
// 3) Завершает звонок (leave)
// 4) Слушает события добавления/удаления Endpoint’ов, плохого соединения и т.д.
export function useVideoCall({
    bookingHash,
    remaining,
    voximplantUserName,
}: Parameters): [CallsValue, CallHandlers] {
    const isMounted = useMountedState()
    const [{ microphoneEnabled, cameraEnabled }] = useInputDevicesContext()

    // Состояния, которые возвращаем наружу
    const [endpoints, endpointsActions] = useMap<Endpoints>({})
    const [currentCall, setCall] = useState<Call>()
    const [error, setError] = useState<Error>()
    const [navigateTo, setNavigateTo] = useState<PageType>()
    const [isMyConnectionBad, setIsMyConnectionBad] = useState(false)
    const [isPeerConnectionBad, setIsPeerConnectionBad] = useState(false)

    // Чтобы внутри хуков и эффектов иметь «живые» ссылки на resolve/reject (при подключении)
    const callRef = useRef<Call>() // текущий звонок из Voximplant
    const callResolveRef = useRef<(call: Call) => void>() // коллбэк, который вызовем при удачном подключении
    const callRejectRef = useRef<() => void>() // коллбэк, который вызовем при ошибке
    const microphoneEnabledRef = useRef<boolean>(false) // флаг микрофона (пока есть известная проблема в Voximplant)
    const analyzeConnectionDelayed = useDelayedConnectionAnalyzer()

    // Инициализируем SDK
    const sdkRef = useRef(VW.getInstance())

    // Когда звонок «подключен»
    const onCallConnected = useCallback(() => {
        const call = callRef.current
        if (!call) return

        // "Фикс" – включить/выключить микрофон (до починки peerConnection на стороне Voximplant)
        if (!microphoneEnabledRef.current) {
            call.muteMicrophone()
        } else {
            call.unmuteMicrophone()
        }

        // Сообщаем наружу, что звонок установился
        callResolveRef.current?.(call)
    }, [])

    // Когда звонок «отключён»
    const onCallDisconnected = useCallback(
        ({ headers }: EventHandlers.Disconnected) => {
            // Отписываем все обработчики
            detachCallHandlers()
            setCall(undefined)
            callRef.current = undefined

            // Если хук ещё не размонтирован – решаем, куда навигировать
            if (isMounted()) {
                if (
                    headers &&
                    "X-Reason" in headers &&
                    headers["X-Reason"] === "NEW_CALL_STARTED"
                ) {
                    setNavigateTo(PageType.Lobby)
                } else {
                    setNavigateTo(PageType.HangUp)
                }
            }
        },
        [isMounted]
    )

    // Когда звонок «провалился» (ошибка/отказ)
    const onCallFailed = useCallback((fail: EventHandlers.Failed) => {
        detachCallHandlers()
        setCall(undefined)
        callRef.current = undefined
        setError(new CallFailedError(fail.reason, fail.code, {}))
        callRejectRef.current?.()
    }, [])

    // Когда добавляется новый Endpoint (другой участник конференции)
    const onCallEndpointAdded = useCallback(
        ({ endpoint }: EventHandlers.EndpointMediaHandler) => {
            endpointsActions.set(endpoint.id, undefined)

            // Слушаем добавление/удаление/обновление потоков
            endpoint.on(
                VW.EndpointEvents.RemoteMediaAdded,
                ({ mediaRenderer, endpoint }: EventHandlers.EndpointMediaHandler) => {
                    // Отрисовываем видео только для «других» (voximplantUserName не совпадает)
                    if (voximplantUserName !== endpoint.userName) {
                        endpointsActions.set(endpoint.id, mediaRenderer)
                    }
                }
            )

            endpoint.on(
                VW.EndpointEvents.RemoteMediaUpdated,
                ({ mediaRenderer }: EventHandlers.EndpointMediaHandler) => {
                    endpointsActions.set(endpoint.id, mediaRenderer)
                }
            )

            endpoint.on(VW.EndpointEvents.RemoteMediaRemoved, () => {
                const mediaRenderer = endpointsActions.get(endpoint.id)
                mediaRenderer?.clear()
                endpointsActions.set(endpoint.id, mediaRenderer)
            })
        },
        [endpointsActions, voximplantUserName]
    )

    // Когда Endpoint «удаляется» (участник покидает конференцию)
    const onCallEndpointRemoved = useCallback(
        ({ endpoint }: EventHandlers.EndpointMediaHandler) => {
            endpointsActions.remove(endpoint.id)
        },
        [endpointsActions]
    )

    // Когда приходят CallStats (статистика канала)
    const onCallStatsReceived = useCallback((callStats: EventHandlers.CallStatsReceived) => {
        const { isMyConnectionBad, isPeerConnectionBad } = analyzeConnectionDelayed(callStats.stats)
        setIsMyConnectionBad(isMyConnectionBad)
        setIsPeerConnectionBad(isPeerConnectionBad)
        speakerIndicator.handler(callStats)
    }, [])

    // Подписывает на события текущего звонка (callRef.current).
    // Вызывается после создания звонка в join().
    function attachCallHandlers() {
        const call = callRef.current
        if (!call) return

        // Вешаем хендлеры
        call.on(VW.CallEvents.Connected, onCallConnected)
        call.on(VW.CallEvents.Disconnected, onCallDisconnected)
        call.on(VW.CallEvents.Failed, onCallFailed)
        call.on(VW.CallEvents.EndpointAdded, onCallEndpointAdded)
        call.on(VW.CallEvents.EndpointRemoved, onCallEndpointRemoved)
        call.on(VW.CallEvents.CallStatsReceived, onCallStatsReceived)
    }

    // Снимает все события с текущего звонка.
    function detachCallHandlers() {
        const call = callRef.current
        if (!call) return

        call.off(VW.CallEvents.Connected, onCallConnected)
        call.off(VW.CallEvents.Disconnected, onCallDisconnected)
        call.off(VW.CallEvents.Failed, onCallFailed)
        call.off(VW.CallEvents.EndpointAdded, onCallEndpointAdded)
        call.off(VW.CallEvents.EndpointRemoved, onCallEndpointRemoved)
        call.off(VW.CallEvents.CallStatsReceived, onCallStatsReceived)
    }

    // Присоединиться к конференции
    const join = useCallback(async () => {
        // Если до этого оставался "живой" звонок, нужно сначала его завершить
        if (callRef.current) {
            await hangupCall()
        }

        // Сбрасываем ошибку и "навигацию", чистим state
        setError(undefined)
        setNavigateTo(undefined)

        microphoneEnabledRef.current = microphoneEnabled

        // Создаём новый звонок
        const newCall = sdkRef.current.callConference({
            number: bookingHash,
            simulcast: true,
            video: {
                sendVideo: cameraEnabled || false,
                receiveVideo: true,
            },
            extraHeaders: {
                "X-Remaining": Math.floor(remaining || 0).toString(),
            },
        })

        // Сохраняем в ref
        callRef.current = newCall

        attachCallHandlers()

        // Возвращаем Promise, чтобы снаружи можно было дождаться успешного соединения
        const promise = new Promise<Call>((resolve, reject) => {
            callResolveRef.current = resolve
            callRejectRef.current = reject
        })

        // Как только на onCallConnected будет вызван callResolveRef.current, state обновится
        // (поэтому дополнительно в onCallConnected делаем setCall(newCall)? – см. ниже)
        // но, поскольку onCallConnected = мы там вызываем callResolve и обновим currentCall
        // после этого уже в промисе можно .then(...) обрабатывать
        promise.then((connectedCall) => {
            // Для синхронизации со стейтом (только когда точно подключились)
            setCall(connectedCall)
        })

        return promise
    }, [cameraEnabled, microphoneEnabled, bookingHash, remaining])

    // Покинуть конференцию
    const leave = useCallback(async () => {
        speakerIndicator.clearAll()
        await hangupCall()
    }, [])

    // Завершает (hangup) текущий звонок.
    // Возвращает Promise, который выполнится, когда звонок точно завершится.
    function hangupCall(): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            const call = callRef.current
            if (!call) {
                resolve() // звонка нет, завершаем
                return
            }

            function onHangupSuccess() {
                call?.off(VW.CallEvents.Disconnected, onHangupSuccess)
                resolve()
            }
            function onHangupFailed(failed: EventHandlers.Failed) {
                call?.off(VW.CallEvents.Failed, onHangupFailed)
                reject(failed)
            }

            call.on(VW.CallEvents.Disconnected, onHangupSuccess)
            call.on(VW.CallEvents.Failed, onHangupFailed)

            // Если звонок уже в состоянии ENDED
            if (call.state() === VoximplantSdk.CallState.ENDED) {
                resolve()
                return
            }

            call.hangup()
        })
    }

    useUnmount(() => {
        // Очищаем все MediaRenderers
        if (callRef.current) {
            callRef.current.getEndpoints().forEach((endpoint) => {
                endpoint.mediaRenderers.forEach((renderer) => {
                    renderer.clear()
                })
            })
        }

        // Если SDK ещё подключен, то отключаем
        const sdk = sdkRef.current
        if (sdk.getClientState() === VW.ClientState.CONNECTED) {
            setCall(undefined)
            sdk.disconnect()
        }
    })

    // MEMO: считаем список endpointId и их количество
    const { endpointIds, endpointsCount } = useMemo(() => {
        const endpointIds = Object.keys(endpoints)
        return {
            endpointIds,
            endpointsCount: endpointIds.length,
        }
    }, [endpoints])

    return [
        {
            error,
            endpoints,
            endpointIds,
            endpointsCount,
            call: currentCall,
            isMyConnectionBad,
            isPeerConnectionBad,
            navigateTo,
        },
        {
            join,
            leave,
        },
    ]
}
